import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '../../core/auth/services/auth.service';
import { SidebarStateService } from './sidebar-state.service';
import { SIDEBAR_ICON_VIEWBOX, SIDEBAR_ICONS } from './sidebar-icons';

interface SidebarItem {
  label: string;
  route?: string;
  exact?: boolean;
  expanded?: boolean;
  permissao?: string;
  icon?: string;
  children?: SidebarItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
})
export class Sidebar implements OnInit {
  constructor(
    public authService: AuthService,
    private router: Router,
    public sidebarState: SidebarStateService
  ) {}

  // Flyout de navegação exibido ao passar o mouse num item com o menu
  // recolhido (renderizado fora do container da sidebar, veja sidebar.html,
  // para não ser cortado pelo overflow:hidden dela).
  hoveredItem: SidebarItem | null = null;
  flyoutTop = 0;
  flyoutLeft = 0;
  private ocultarFlyoutTimeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly icons = SIDEBAR_ICONS;
  readonly iconViewBox = SIDEBAR_ICON_VIEWBOX;

  // Em telas de celular a sidebar vira um drawer fechado por padrao (ver
  // sidebar.css) - so abre por cima do conteudo ao tocar no botao do header.
  // Nao mexe na preferencia salva do usuario no desktop (ver
  // SidebarStateService.colapsarPorViewport).
  private readonly consultaMobile = window.matchMedia('(max-width: 768px)');

  private readonly menuOriginal: SidebarItem[] = [
    {
      label: 'Dashboard',
      route: '/dashboard',
      exact: true,
      permissao: 'Dashboard',
      icon: 'dashboard'
    },
    {
      label: 'Dashboard Técnico',
      route: '/dashboard-tec',
      exact: true,
      permissao: 'DashboardTec',
      icon: 'monitoring'
    },
    {
      // Perto do topo de propósito: é onde o analista olha quando o sininho
      // acende, e alerta que fica escondido no fim do menu não é alerta.
      label: 'Alertas',
      route: '/alertas',
      exact: true,
      permissao: 'Operacoes',
      icon: 'notifications'
    },
    {
      //Aba Cadastros
      label: 'Cadastros',
      expanded: false,
      icon: 'folder_open',
      children: [
        {
          // Junta os antigos Investidor e Incorporador (papéis da empresa).
          label: 'Empresa',
          route: '/empresa',
          exact: true,
          permissao: 'Investidores',
          icon: 'business'
        },
        {
          label: 'Empreendimentos',
          route: '/empreendimentos',
          exact: false,
          permissao: 'Empreendimentos',
          icon: 'home_work'
        },
        {
          label: 'Contas',
          route: '/cadastro-contas',
          exact: false,
          permissao: 'Empreendimentos',
          icon: 'account_balance_wallet'
        },
        {
          label: 'Operações',
          route: '/cadastro-operacoes',
          exact: false,
          permissao: 'Empreendimentos',
          icon: 'request_quote'
        },
        
        

      ]
    },
    {
      //Aba Gerenciar Acessos
      label: 'Gerenciar Acessos',
      expanded: false,
      permissao: 'GerenciarAcesso',
      icon: 'admin_panel_settings',
      children: [
        {
          label: 'Perfil',
          route: '/perfil',
          exact: false,
          icon: 'badge'
        },
        {
          label: 'Permissões',
          route: '/permissoes',
          exact: false,
          icon: 'lock'
        },
        {
          label: 'Sessão Empreend.',
          route: '/sessoes-operacao',
          exact: false,
          icon: 'sessaoEmpreendimento'
        }
      ]
    },
    {
      label: 'Credenciais de Operador',
      route: '/credenciais-operador',
      exact: true,
      permissao: 'CredenciaisOperador',
      icon: 'lock'
    },
    {
      label: 'Conexões Sienge',
      route: '/integracoes-sienge',
      exact: true,
      permissao: 'IntegracaoSienge',
      icon: 'link'
    },
    {

      //Aba Operações
      label: 'Operações',
      expanded: false,
      icon: 'payments',
      children: [
        {
          label: 'Diferenças',
          route: '/operacoes/empreendimento',
          exact: false,
          permissao: 'Operacoes',
          icon: 'home_work'
        },
        {
          label: 'Espelho de Vendas',
          route: '/operacoes/espelho-vendas',
          exact: false,
          permissao: 'EspelhoVendas',
          icon: 'apartment'
        },
        // Sem `route`: aparecem no menu mas ainda não navegam para lugar
        // nenhum - as telas não existem. O template renderiza item sem rota
        // como texto, não como link (ver sidebar.html), então não há href
        // morto nem rota inventada esperando implementação.
        {
          label: 'BDados',
          permissao: 'Operacoes',
          icon: 'folder_open'
        },
        {
          label: 'Carteira',
          permissao: 'Operacoes',
          icon: 'account_balance_wallet'
        },
        {
          label: 'Fluxo de Caixa',
          permissao: 'Operacoes',
          icon: 'savings'
        }
      ]
    },
    {
      label: 'Monitoramento',
      expanded: false,
      icon: 'visibility',
      children: [
        {
          label: 'Empreendimento',
          route: '/carteira',
          exact: true,
          permissao: 'Monitoramento',
          icon: 'home_work'
        },
        {
          label: 'Carteira',
          route: '/carteira',
          exact: true,
          permissao: 'Monitoramento',
          icon: 'account_balance_wallet'
        }
      ]
    },
    {
      label: 'Relatórios',
      route: '/relatorios',
      exact: true,
      permissao: 'Relatorios',
      icon: 'assessment'
    },
    {
      label: 'Convert',
      expanded: false,
      icon: 'find_in_page',
      children: [
        {
          label: 'Empreendimento',
          route: '/extrator/empreendimento',
          exact: false,
          permissao: 'Extrator',
          icon: 'home_work'
        }
      ]
    },
    {
      // Mapeamento: leiautes que ainda não têm regra de conversão no sistema.
      label: 'Mapeamento',
      expanded: false,
      icon: 'description',
      children: [
        {
          label: 'Novas Conversões',
          route: '/mapeamento/novas-conversoes',
          exact: true,
          // Tela própria (antes usava a do Convert e aparecia para todos com Convert).
          permissao: 'NovasConversoes',
          icon: 'upload_file'
        },
        {
          // Equipe de inovação: valida as regras criadas pela IA em Novas Conversões.
          label: 'Validação de Regras de Conversão',
          route: '/mapeamento/validacao-regras',
          exact: true,
          permissao: 'ValidacaoParsers',
          icon: 'fact_check'
        }
      ]
    },
    // {
    //   label: 'Gerenciar Vínculos',
    //   route: '/vinculos',
    //   exact: true,
    //   permissao: 'GerenciamentoVinculos',
    //   icon: 'link'
    // },
    {
      label: 'Auditor de Contrato',
      route: '/auditor-contrato',
      exact: true,
      permissao: 'AuditorContrato',
      icon: 'fact_check'
    },
    {
      label: 'Auditor Financeiro',
      route: '/auditor-financeiro',
      exact: true,
      permissao: 'AuditorFinanceiro',
      icon: 'account_balance'
    },
    {
      label: 'Engenharia',
      expanded: false,
      icon: 'apartment',
      children: [
        {
          label: 'Auditor Operações Financeiras',
          route: '/engenharia/auditor-fiscal',
          exact: true,
          permissao: 'AuditorFiscal',
          icon: 'fact_check'
        },
        {
          label: 'Contas Pagas',
          route: '/engenharia/contas-pagas',
          exact: true,
          permissao: 'ContasPagas',
          icon: 'payments'
        },
        {
          label: 'Conciliador de Notas',
          route: '/engenharia/conciliador-notas',
          exact: true,
          permissao: 'ConciliadorNotas',
          icon: 'receipt_long'
        }
      ]
    },
    {
      label: 'Monitor Obras',
      expanded: false,
      icon: 'home_work',
      children: [
        {
          label: 'Upload de Arquivos',
          route: '/monitor-obras/upload-arquivos',
          exact: true,
          permissao: 'MonitorObrasUpload',
          icon: 'description'
        },
        {
          label: 'Consulta',
          route: '/monitor-obras/consulta',
          exact: true,
          permissao: 'MonitorObrasConsulta',
          icon: 'visibility'
        }
      ]
    },
    //IMPORTAÇÃO DO CORE
    {
      label: 'Importação',
      expanded: false,
      icon: 'upload',
      children: [
        {
          label: 'Importar BRM',
          route: '/importacao/importar-brm',
          exact: true,
          permissao: 'ImportacaoBRM',
          icon: 'upload_file'
        },
        {
          label: 'Importar Operação',
          route: '/importacao/importar-operacao',
          exact: true,
          permissao: 'ImportacaoOperacao',
          icon: 'request_quote'
        }
      ]
    },
    //VISTOMOB (vistoria/medição física de obra pelo app mobile)
    {
      label: 'VistoMob',
      expanded: false,
      icon: 'photo_camera',
      children: [
        {
          label: 'Cenários PMC',
          route: '/vistomob/cenarios',
          exact: true,
          permissao: 'VistoMob',
          icon: 'upload_file'
        },
        {
          // exact: false para continuar marcado no detalhe (/vistomob/medicoes/:id).
          label: 'Medições',
          route: '/vistomob/medicoes',
          exact: false,
          permissao: 'VistoMob',
          icon: 'fact_check'
        },
        {
          label: 'Vistoriadores',
          route: '/vistomob/vistoriadores',
          exact: true,
          permissao: 'VistoMob',
          icon: 'badge'
        }
      ]
    },
    //API EXTERNA (consumo dos dados do BRM por terceiros)
    {
      label: 'API Externa',
      expanded: false,
      icon: 'link',
      children: [
        {
          label: 'Usuários externos',
          route: '/api-externa/usuarios',
          exact: true,
          permissao: 'UsuariosExternos',
          icon: 'badge'
        },
        {
          label: 'Vincular acessos',
          route: '/api-externa/vinculos',
          exact: true,
          permissao: 'UsuariosExternos',
          icon: 'admin_panel_settings'
        },
        {
          label: 'Consumo da API',
          route: '/api-externa/consumo',
          exact: true,
          permissao: 'ConsumoApiExterna',
          icon: 'assessment'
        },
        {
          label: 'Documentação',
          route: '/api-externa/documentacao',
          exact: true,
          permissao: 'UsuariosExternos',
          icon: 'description'
        }
      ]
    },
    //CONFIGURAÇÕES
    {
      label: 'Configurações',
      expanded: false,
      icon: 'settings',
      children: [
         {
            label: 'Logs',
            route: '/logs',
            exact: true,
            permissao: 'Log',
            icon: 'history'
        },
        {
          label: 'Feriados',
          route: '/feriados',
          exact: true ,
          permissao: 'Feriados',
          icon: 'calendar_month'
        },
        {
          label: 'Escopos',
          route: '/escopos',
          exact: true,
          permissao: 'Empreendimentos',
          icon: 'checklist'
        },
        {
          label: 'Classif. Contas Pagas',
          route: '/classificacoes-contas-pagas',
          exact: true ,
          permissao: 'ClassificacoesContasPagas',
          icon: 'category'
        },
        {
          label: 'Gerenciar Vínculos',
          route: '/vinculos',
          exact: true,
          permissao: 'GerenciamentoVinculos',
          icon: 'link'
        },
        {
          label: 'Arq. Não Mapeados',
          route: '/extratos-bancarios-nao-identificados',
          exact: true,
          permissao: 'ArquivosNaoMapeados',
          icon: 'description'
        },
      ]
    }
  ];

  menuItems: SidebarItem[] = this.menuOriginal;

  ngOnInit(): void {
    this.authService.currentUser$.subscribe(user => {
      this.menuItems = user
        ? this.filtrarMenuPorPermissao(this.menuOriginal)
        : this.menuOriginal;

      this.sincronizarMenuExpandidoPorRota();
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.sincronizarMenuExpandidoPorRota();

        // No celular a sidebar e um drawer por cima do conteudo - fecha
        // sozinho ao navegar, senao ficaria aberto cobrindo a pagina nova.
        if (this.consultaMobile.matches) {
          this.sidebarState.colapsarPorViewport();
        }
      });

    if (this.consultaMobile.matches) {
      this.sidebarState.colapsarPorViewport();
    }
    this.consultaMobile.addEventListener('change', (evento) => {
      if (evento.matches) {
        this.sidebarState.colapsarPorViewport();
      }
    });
  }

  // Cabecalho de grupo (ex.: "Monitor Obras"): com o menu expandido continua
  // o acordeao normal; com o menu recolhido, so o hover abre o flyout (não
  // existe em touch), entao o toque também abre/fecha o flyout aqui.
  onGrupoClick(event: MouseEvent, item: SidebarItem): void {
    if (this.sidebarState.collapsed) {
      if (this.hoveredItem === item) {
        this.fecharFlyout();
      } else {
        this.onItemMouseEnter(event, item);
      }
      return;
    }

    this.toggleMenu(item);
  }

  private filtrarMenuPorPermissao(items: SidebarItem[]): SidebarItem[] {
    return items
      .map(item => {
        if (item.permissao && !this.authService.hasPermission(item.permissao)) {
          return null;
        }

        if (item.children) {
          const filhosFiltrados = this.filtrarMenuPorPermissao(item.children);

          return {
            ...item,
            children: filhosFiltrados
          };
        }

        return item;
      })
      .filter(item => item && (!item.children || item.children.length > 0)) as SidebarItem[];
  }

  toggleMenu(item: SidebarItem): void {
    if (!item.children) {
      return;
    }

    this.menuItems = this.menuItems.map(menuItem => {
      if (!menuItem.children) {
        return menuItem;
      }

      if (menuItem === item) {
        return {
          ...menuItem,
          expanded: !menuItem.expanded
        };
      }

      return {
        ...menuItem,
        expanded: false
      };
    });
  }

  isGrupoAtivo(item: SidebarItem): boolean {
    return item.children?.some(child => this.isRotaAtiva(child)) ?? false;
  }

  private sincronizarMenuExpandidoPorRota(): void {
    this.menuItems = this.menuItems.map(item => {
      if (!item.children) {
        return item;
      }

      return {
        ...item,
        expanded: item.children.some(child => this.isRotaAtiva(child))
      };
    });
  }

  private isRotaAtiva(item: SidebarItem): boolean {
    if (!item.route) {
      return false;
    }

    return this.router.isActive(item.route, item.exact ?? false);
  }

  onItemMouseEnter(event: MouseEvent, item: SidebarItem): void {
    this.cancelarOcultacaoFlyout();

    const alvo = event.currentTarget as HTMLElement;
    const rect = alvo.getBoundingClientRect();
    this.flyoutTop = rect.top;
    this.flyoutLeft = rect.right + 8;
    this.hoveredItem = item;
  }

  onItemMouseLeave(): void {
    this.agendarOcultacaoFlyout();
  }

  onFlyoutMouseEnter(): void {
    this.cancelarOcultacaoFlyout();
  }

  onFlyoutMouseLeave(): void {
    this.agendarOcultacaoFlyout();
  }

  fecharFlyout(): void {
    this.cancelarOcultacaoFlyout();
    this.hoveredItem = null;
  }

  private agendarOcultacaoFlyout(): void {
    this.cancelarOcultacaoFlyout();
    this.ocultarFlyoutTimeoutId = setTimeout(() => {
      this.hoveredItem = null;
    }, 150);
  }

  private cancelarOcultacaoFlyout(): void {
    if (this.ocultarFlyoutTimeoutId) {
      clearTimeout(this.ocultarFlyoutTimeoutId);
      this.ocultarFlyoutTimeoutId = null;
    }
  }
}